import { api, apiStream } from './api';

export interface FeedEvidence {
  signal_id: string;
  platform: string;
  url: string | null;
  text: string;
}

export interface FeedCluster {
  cluster_id: string;
  cluster_version: number;
  title: string;
  summary: string | null;
  topics: string[];
  entities: string[];
  platforms: string[];
  category: string | null;
  region: string | null;
  language: string | null;
  signal_count: number;
  first_seen_at: string;
  last_seen_at: string;
  velocity: { '1h': number; '6h': number; '24h': number };
  trend_score: number;
  score_explanation: {
    version?: string;
    components?: Record<string, number>;
    weights?: Record<string, number>;
    window_counts?: Record<string, number>;
  };
  evidence: FeedEvidence[];
  schema_version: number;
}

export interface FeedResponse {
  items: FeedCluster[];
  next_cursor: string | null;
  as_of: string;
}

export interface FeedSignal {
  id: string;
  platform: string;
  type: string;
  text: string;
  url: string | null;
  published_at: string | null;
  captured_at: string;
  similarity: number;
  membership_reason: Record<string, unknown>;
}

export interface ConnectorStatus {
  id: string;
  display_name: string;
  category: string;
  source_class?: string;
  data_delay?: string;
  original_source?: string | null;
  access_source?: string | null;
  post_etl_status?: 'not_connected';
  language?: string | null;
  region?: string | null;
  source_url?: string | null;
  public_access?: boolean;
  enabled: boolean;
  capabilities: Record<string, boolean>;
  health?: {
    status: string;
    message: string;
    checked_at?: string;
    latency_ms?: number | null;
  };
}

export interface ConnectorConfig {
  connector_id: string;
  enabled: boolean;
  frequency_seconds: number;
  api_tokens: Record<string, string>;
  options: Record<string, unknown>;
  version_id?: string;
  version_number?: number;
  updated_at?: string;
}

export interface ConnectorCostMetrics {
  connector_id: string;
  provider_id: string;
  enabled: boolean;
  measurement_status: 'not_instrumented';
  total_calls_24h: number | null;
  total_calls_month: number | null;
  estimated_cost_usd: number | null;
  estimated_cost_brl: number | null;
  cache_hit_rate: number | null;
  quota_limit_percent: number | null;
}

export interface ConnectorLoadResponse {
  run: {
    run_id: string;
    trace_id: string;
    connector_id: string;
    connector_kind: 'fact' | 'signal';
    stage: 'pre_etl';
    status: 'completed' | 'partial' | 'timed_out';
    schedule_enabled: boolean;
    persisted: false;
    post_etl_status?: 'not_connected';
    started_at: string;
    completed_at: string;
    duration_ms: number;
    record_count: number;
    bytes: number;
    limit: number;
    max_bytes: number;
    timeout_seconds: number;
    truncated: boolean;
    truncated_reason: 'limit' | 'max_bytes' | 'timeout' | null;
  };
  records: unknown[];
}

export interface FeedFilters {
  platforms?: string[];
  topic?: string;
  entity?: string;
  category?: string;
  region?: string;
  language?: string;
  minScore?: number;
  window?: string;
  cursor?: string | null;
}

export type PoolPriority = 'background' | 'normal' | 'high' | 'urgent' | 'critical';
export type WireFactLifecycle =
  | 'observed'
  | 'preliminary'
  | 'active'
  | 'revised'
  | 'retracted'
  | 'cancelled';
export type EpistemicClass =
  | 'measurement'
  | 'official_record'
  | 'publication_event'
  | 'attributed_statement'
  | 'transaction'
  | 'calculated_change'
  | 'derived_assertion';

export interface PoolEvent {
  schema_id: 'wire_pool_event@1';
  pool_event_id: string;
  event_name: string;
  partition_key: string;
  sequence: number;
  idempotency_key: string;
  ordering_key: string | null;
  wire_fact_id: string | null;
  wire_id: string | null;
  connector_id: string | null;
  occurred_at: string;
  emitted_at: string;
  trace_id: string;
  payload_ref: string;
  payload_schema_id: string | null;
  priority: PoolPriority;
  alert_reason_codes: string[];
  access_tags: string[];
  replayable: boolean;
}

export interface PoolEventsResponse {
  items: PoolEvent[];
  next_cursor: string | null;
  has_more: boolean;
}

export interface PoolEventFilters {
  cursor?: string | null;
  limit?: number;
  eventName?: string;
  partitionKey?: string;
  priority?: PoolPriority;
}

export interface WireFactTime {
  event_at: string | null;
  period_start: string | null;
  period_end: string | null;
  source_published_at: string | null;
  source_updated_at: string | null;
  first_observed_at: string;
  ingested_at: string;
  normalized_at: string;
  valid_from: string | null;
  valid_to: string | null;
  system_from: string;
  system_to: string | null;
  timestamp_quality: string;
  delay: {
    source_seconds: number | null;
    collection_seconds: number | null;
    etl_seconds: number | null;
    pool_seconds: number | null;
    confidence: string;
  };
}

export interface WireFact {
  schema_id: 'wire_fact@1';
  wire_fact_id: string;
  aggregate_key: string;
  version: number;
  supersedes_version?: number | null;
  wire_ids?: string[];
  lifecycle_status: WireFactLifecycle;
  epistemic_class: EpistemicClass;
  evidence_status: 'single_source' | 'independently_supported' | 'conflicted' | 'unknown';
  fact: {
    fact_type: string;
    subject: { entity_id: string; entity_type: string; label: string; namespace?: string | null };
    predicate: string;
    object: {
      value_type: string;
      value: string | number | boolean | null;
      unit?: string | null;
      currency?: string | null;
      previous_value?: string | number | boolean | null;
      change_basis?: string | null;
    };
    dimensions: Record<string, unknown>;
    geography?: {
      country_code?: string | null;
      subdivision?: string | null;
      place_id?: string | null;
    };
  };
  time: WireFactTime;
  rights: {
    class: 'R0' | 'R1' | 'R2' | 'RX';
    display_metadata: boolean;
    display_excerpt: boolean;
    display_full_text: boolean;
    derive: boolean;
    publish: boolean;
    attribution_required: boolean;
    retention_policy_id: string;
  };
  quality: {
    novelty: number | null;
    materiality: number | null;
    confidence: number | null;
    human_review_status: string;
  };
  created_at: string;
}

export interface WireFactVersion {
  version: number;
  supersedes_version: number | null;
  valid_from: string | null;
  valid_to: string | null;
  system_from: string;
  system_to: string | null;
  normalized_at: string;
}

export interface WireEvidence {
  evidence_id: string;
  role: string;
  authority_class: string;
  supports_scope: string;
  connector_id: string;
  source_id: string;
  url: string | null;
  sha256: string;
  byte_size: number;
  observed_at: string;
  raw_available: boolean;
}

export interface WireCoverage {
  coverage_id: string;
  relation: string;
  method: string;
  confidence: number | null;
  publisher: string;
  country_code: string | null;
  language: string | null;
  title: string;
  url: string;
  published_at: string | null;
  rights_class: string;
}

export interface WireEnrichment {
  enrichment_id: string;
  task: string;
  method: string;
  provider: string | null;
  model: string | null;
  pipeline_version: string;
  prompt_version: string | null;
  status: 'completed' | 'degraded' | 'failed' | 'held';
  output: unknown;
  confidence: number | null;
  cost_usd: number | null;
  created_at: string;
}

export interface Wire {
  schema_id: 'wire@1';
  wire_id: string;
  version: number;
  status: 'open' | 'watching' | 'closed' | 'reopened' | 'merged' | 'split' | 'tombstoned';
  title: string;
  categories: string[];
  entity_ids: string[];
  countries: string[];
  languages: string[];
  first_activity_at: string;
  last_activity_at: string;
  merged_into_wire_id: string | null;
  split_from_wire_id: string | null;
  members: Array<{
    membership_id: string;
    member_type: string;
    member_id: string;
    role: string;
    method: string;
    confidence: number | null;
    reason_codes: string[];
    added_at: string;
    removed_at: string | null;
  }>;
  member_page: { returned: number; total: number; complete: boolean; next_cursor: string | null };
  quality: {
    novelty?: number | null;
    materiality?: number | null;
    confidence?: number | null;
    conflict_count?: number;
  };
  created_at: string;
  updated_at: string;
}

export interface PoolSourceHealth {
  connector_id: string;
  enabled: boolean;
  status: string;
  last_observed_at: string | null;
  last_ingested_at: string | null;
  error_code: string | null;
  error_detail: string | null;
  lag_seconds?: number | null;
  gap_detected?: boolean;
  item_count?: number;
  byte_count?: number;
  cost_usd?: number | null;
}

export interface PoolHealth {
  status?: 'healthy' | 'degraded' | 'gap' | 'paused' | string;
  lag_seconds?: number | null;
  gap_detected?: boolean;
  replay_in_progress?: boolean;
  last_event_at?: string | null;
  latest_activity_kind?: 'event' | 'ingestion';
  sources: PoolSourceHealth[];
}

export interface TenantSubscription {
  id: string;
  name: string;
  status: string;
  description?: string | null;
  accepted_schema_major: number;
  event_types: string[];
  filters: Record<string, unknown>;
  minimum_authority?: string | null;
  epistemic_classes: string[];
  max_freshness_seconds?: number | null;
  cursor?: string | null;
  last_event_at?: string | null;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface TenantSubscriptionInput {
  name: string;
  description?: string | null;
  accepted_schema_major?: number;
  event_types: string[];
  filters: Record<string, unknown>;
  minimum_authority?: string | null;
  epistemic_classes?: string[];
  max_freshness_seconds?: number | null;
}

export interface TenantAutomation {
  id: string;
  name: string;
  subscription_id: string;
  trigger_id?: string | null;
  curation_id?: string | null;
  status: string;
  kill_switch: boolean;
  mode: 'shadow' | 'supervised';
  event_types: string[];
  filters: Record<string, unknown>;
  model_policy: Record<string, unknown>;
  prompt_version?: string | null;
  destination: Record<string, unknown>;
  budget: Record<string, unknown>;
  owner: string;
  error_channel?: string | null;
  automatic_publication: false;
  created_at?: string;
  updated_at?: string;
}

export interface TenantAutomationInput {
  name: string;
  subscription_id: string;
  trigger_id?: string | null;
  curation_id?: string | null;
  mode: 'shadow' | 'supervised';
  event_types?: string[];
  filters?: Record<string, unknown>;
  model_policy?: Record<string, unknown>;
  prompt_version?: string | null;
  destination?: Record<string, unknown>;
  budget?: Record<string, unknown>;
  owner: string;
  error_channel?: string | null;
}

export interface TenantWatchlistEntry {
  entity_id: string;
  entity_type?: string | null;
  label?: string | null;
  aliases: string[];
}

export interface TenantWatchlist {
  id: string;
  name: string;
  description?: string | null;
  entries: TenantWatchlistEntry[];
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface TenantWatchlistInput {
  name: string;
  description?: string | null;
  entries: TenantWatchlistEntry[];
  enabled?: boolean;
}

export interface TenantAutomationKillSwitch {
  engaged: boolean;
  reason: string | null;
  changed_by: string | null;
  changed_at: string | null;
  total_automations: number;
  active_automations: number;
  requires_individual_resume: true;
  automatic_publication: false;
  affected_automations?: number;
}

export interface TenantTriggerRule {
  event_names?: string[];
  partition_keys?: string[];
  connector_ids?: string[];
  priorities?: PoolPriority[];
  required_alert_reason_codes?: string[];
}

export interface TenantTriggerRevisionPolicy {
  on_revised?: 'accept' | 'hold';
  on_retracted?: 'hold';
}

export interface TenantTrigger {
  id: string;
  subscription_id: string;
  watchlist_id?: string | null;
  name: string;
  status: 'active' | 'paused';
  rule: TenantTriggerRule;
  revision_policy: TenantTriggerRevisionPolicy;
  created_at?: string;
  updated_at?: string;
}

export interface TenantTriggerInput {
  subscription_id: string;
  watchlist_id?: string | null;
  name: string;
  rule?: TenantTriggerRule;
  revision_policy?: TenantTriggerRevisionPolicy;
}

export interface TenantCuration {
  id: string;
  subscription_id: string;
  name: string;
  instructions: string;
  filters: Record<string, unknown>;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface TenantCurationInput {
  subscription_id: string;
  name: string;
  instructions?: string;
  filters?: Record<string, unknown>;
}

export type AutomationRunStatus = 'held' | 'completed';
export type AutomationRunOutcome = 'would_observe' | 'would_propose' | 'held';

export interface AutomationRun {
  id: string;
  automation_id: string;
  pool_event_id: string | null;
  idempotency_key: string;
  status: AutomationRunStatus;
  decision: {
    outcome: AutomationRunOutcome;
    matched: boolean;
    reason_codes: string[];
    event: PoolEvent | null;
    trigger_id: string | null;
    curation_id: string | null;
    side_effects: false;
    next_cursor?: string | null;
    has_more?: boolean;
  };
  cost:
    | { status: 'not_instrumented' }
    | { status: 'instrumented'; amount_usd: string; [key: string]: unknown };
  error: Record<string, unknown> & { code?: string; message?: string; action?: string };
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  automatic_publication: false;
}

export interface AutomationCostReport {
  status: 'not_instrumented' | 'instrumented';
  currency: 'USD' | null;
  total_usd: string | null;
  runs: number;
  instrumented_runs: number;
  by_automation: Array<{
    automation_id: string;
    total_usd: string;
    instrumented_runs: number;
  }>;
}

function feedQuery(filters: FeedFilters): string {
  const query = new URLSearchParams();
  for (const platform of filters.platforms ?? []) query.append('platform', platform);
  if (filters.topic) query.append('topic', filters.topic);
  if (filters.entity) query.append('entity', filters.entity);
  if (filters.category) query.set('category', filters.category);
  if (filters.region) query.set('region', filters.region);
  if (filters.language) query.set('language', filters.language);
  query.set('min_score', String(filters.minScore ?? 0));
  query.set('window', filters.window ?? '24h');
  if (filters.cursor) query.set('cursor', filters.cursor);
  return query.toString();
}

function poolEventQuery(filters: PoolEventFilters): string {
  const query = new URLSearchParams();
  if (filters.cursor) query.set('cursor', filters.cursor);
  if (filters.limit) query.set('limit', String(filters.limit));
  if (filters.eventName) query.append('event_name', filters.eventName);
  if (filters.partitionKey) query.set('partition_key', filters.partitionKey);
  if (filters.priority) query.set('priority', filters.priority);
  const value = query.toString();
  return value ? `?${value}` : '';
}

export const feedDriveService = {
  getFeed: (filters: FeedFilters = {}): Promise<FeedResponse> =>
    api.get<FeedResponse>(`/feeddrive/feed?${feedQuery(filters)}`),
  getClusterSignals: (clusterId: string): Promise<FeedSignal[]> =>
    api.get<FeedSignal[]>(`/feeddrive/clusters/${clusterId}/signals`),
  getConnectors: (): Promise<ConnectorStatus[]> =>
    api.get<ConnectorStatus[]>('/feeddrive/connectors'),
  getConnectorConfig: (connectorId: string): Promise<ConnectorConfig> =>
    api.get<ConnectorConfig>(`/feeddrive/connectors/${connectorId}/config`),
  updateConnectorConfig: (
    connectorId: string,
    payload: Partial<ConnectorConfig>,
  ): Promise<ConnectorConfig> =>
    api.put<ConnectorConfig>(`/feeddrive/connectors/${connectorId}/config`, payload),
  getConnectorMetricsCosts: (): Promise<ConnectorCostMetrics[]> =>
    api.get<ConnectorCostMetrics[]>('/feeddrive/connectors/metrics/costs'),
  loadConnector: (
    connectorId: string,
    settings: { limit?: number; timeoutSeconds?: number; maxBytes?: number } = {},
  ): Promise<ConnectorLoadResponse> =>
    api.post<ConnectorLoadResponse>(`/feeddrive/connectors/${connectorId}/load`, {
      limit: settings.limit ?? 10,
      timeout_seconds: settings.timeoutSeconds ?? 30,
      max_bytes: settings.maxBytes ?? 8_000_000,
    }),
  getPoolEvents: (filters: PoolEventFilters = {}): Promise<PoolEventsResponse> =>
    api.get<PoolEventsResponse>(`/feeddrive/pool/events${poolEventQuery(filters)}`),
  streamPoolEvents: async (
    filters: PoolEventFilters,
    onEvent: (event: PoolEvent) => void,
    signal: AbortSignal,
    lastEventId?: string,
    onOpen?: () => void,
  ): Promise<void> => {
    const response = await apiStream(`/feeddrive/pool/stream${poolEventQuery(filters)}`, {
      method: 'GET',
      signal,
      headers: lastEventId ? { 'Last-Event-ID': lastEventId } : undefined,
    });
    if (!response.body) throw new Error('NEWS POOL stream sem corpo de resposta.');
    onOpen?.();
    const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = '';
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += value;
      const frames = buffer.split(/\r?\n\r?\n/);
      buffer = frames.pop() ?? '';
      for (const frame of frames) {
        const lines = frame.split(/\r?\n/);
        const eventType =
          lines
            .find((line) => line.startsWith('event:'))
            ?.slice(6)
            .trim() ?? 'message';
        const data = lines
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trimStart())
          .join('\n');
        if (!data) continue;
        if (eventType === 'upstream_error') {
          let detail: { status?: unknown; service?: unknown } = {};
          try {
            detail = JSON.parse(data) as { status?: unknown; service?: unknown };
          } catch {
            // A mensagem operacional continua genérica quando o upstream envia JSON inválido.
          }
          const safe = (value: unknown) =>
            typeof value === 'string' && /^[\w.-]{1,80}$/.test(value) ? value : null;
          const service = safe(detail.service);
          const status = safe(detail.status);
          throw new Error(
            `NEWS POOL upstream indisponível${service ? ` · ${service}` : ''}${status ? ` · ${status}` : ''}.`,
          );
        }
        const parsed = JSON.parse(data) as PoolEvent;
        if (parsed.schema_id === 'wire_pool_event@1') onEvent(parsed);
      }
    }
    if (!signal.aborted) throw new Error('NEWS POOL stream desconectado pelo servidor.');
  },
  getPoolHealth: async (): Promise<PoolHealth> => {
    const response = await api.get<PoolHealth | PoolSourceHealth[]>('/feeddrive/pool/health');
    if (!Array.isArray(response)) return response;
    const enabled = response.filter((source) => source.enabled);
    const degraded = enabled.some(
      (source) => !['ok', 'healthy', 'completed'].includes(source.status),
    );
    const gap = enabled.some((source) => source.gap_detected);
    const timestamps = response
      .map((source) => source.last_ingested_at)
      .filter((value): value is string => Boolean(value))
      .sort();
    return {
      status:
        response.length === 0 || enabled.length === 0
          ? 'unknown'
          : gap
            ? 'gap'
            : degraded
              ? 'degraded'
              : 'healthy',
      gap_detected: gap,
      lag_seconds:
        enabled.length > 0
          ? Math.max(0, ...enabled.map((source) => source.lag_seconds ?? 0))
          : null,
      last_event_at: timestamps.at(-1) ?? null,
      latest_activity_kind: 'ingestion',
      sources: response,
    };
  },
  getWireFact: (id: string): Promise<WireFact> => api.get<WireFact>(`/feeddrive/wire-facts/${id}`),
  getWireFactVersions: (id: string): Promise<WireFactVersion[]> =>
    api.get<WireFactVersion[]>(`/feeddrive/wire-facts/${id}/versions`),
  getWireFactEvidence: (id: string): Promise<WireEvidence[]> =>
    api.get<WireEvidence[]>(`/feeddrive/wire-facts/${id}/evidence`),
  getWireFactCoverage: (id: string): Promise<WireCoverage[]> =>
    api.get<WireCoverage[]>(`/feeddrive/wire-facts/${id}/coverage`),
  getWireFactEnrichments: (id: string): Promise<WireEnrichment[]> =>
    api.get<WireEnrichment[]>(`/feeddrive/wire-facts/${id}/enrichments`),
  getWire: (id: string): Promise<Wire> => api.get<Wire>(`/feeddrive/wires/${id}`),
  getWireTimeline: (id: string): Promise<PoolEvent[]> =>
    api.get<PoolEvent[]>(`/feeddrive/wires/${id}/timeline`),
  getSubscriptions: (): Promise<TenantSubscription[]> =>
    api.get<TenantSubscription[]>('/feeddrive/subscriptions'),
  createSubscription: (payload: TenantSubscriptionInput): Promise<TenantSubscription> =>
    api.post<TenantSubscription>('/feeddrive/subscriptions', payload),
  updateSubscription: (
    id: string,
    payload: Partial<TenantSubscriptionInput>,
  ): Promise<TenantSubscription> =>
    api.patch<TenantSubscription>(`/feeddrive/subscriptions/${id}`, payload),
  setSubscriptionPaused: (id: string, paused: boolean): Promise<TenantSubscription> =>
    api.patch<TenantSubscription>(`/feeddrive/subscriptions/${id}/pause`, { paused }),
  deleteSubscription: async (id: string): Promise<void> => {
    await api.delete<unknown>(`/feeddrive/subscriptions/${id}`);
  },
  getAutomations: (): Promise<TenantAutomation[]> =>
    api.get<TenantAutomation[]>('/feeddrive/automations'),
  createAutomation: (payload: TenantAutomationInput): Promise<TenantAutomation> =>
    api.post<TenantAutomation>('/feeddrive/automations', payload),
  updateAutomation: (
    id: string,
    payload: Partial<TenantAutomationInput>,
  ): Promise<TenantAutomation> =>
    api.patch<TenantAutomation>(`/feeddrive/automations/${id}`, payload),
  setAutomationPaused: (id: string, paused: boolean): Promise<TenantAutomation> =>
    api.patch<TenantAutomation>(`/feeddrive/automations/${id}/pause`, { paused }),
  deleteAutomation: async (id: string): Promise<void> => {
    await api.delete<unknown>(`/feeddrive/automations/${id}`);
  },
  getAutomationKillSwitch: (): Promise<TenantAutomationKillSwitch> =>
    api.get<TenantAutomationKillSwitch>('/feeddrive/automations/kill-switch'),
  setAutomationKillSwitch: (
    engaged: boolean,
    reason: string,
  ): Promise<TenantAutomationKillSwitch> =>
    api.patch<TenantAutomationKillSwitch>('/feeddrive/automations/kill-switch', {
      engaged,
      reason,
    }),
  getWatchlists: (): Promise<TenantWatchlist[]> =>
    api.get<TenantWatchlist[]>('/feeddrive/watchlists'),
  createWatchlist: (payload: TenantWatchlistInput): Promise<TenantWatchlist> =>
    api.post<TenantWatchlist>('/feeddrive/watchlists', payload),
  updateWatchlist: (id: string, payload: Partial<TenantWatchlistInput>): Promise<TenantWatchlist> =>
    api.patch<TenantWatchlist>(`/feeddrive/watchlists/${id}`, payload),
  setWatchlistEnabled: (id: string, enabled: boolean): Promise<TenantWatchlist> =>
    api.patch<TenantWatchlist>(`/feeddrive/watchlists/${id}/enabled`, { enabled }),
  deleteWatchlist: async (id: string): Promise<void> => {
    await api.delete<unknown>(`/feeddrive/watchlists/${id}`);
  },
  getTriggers: (): Promise<TenantTrigger[]> => api.get<TenantTrigger[]>('/feeddrive/triggers'),
  createTrigger: (payload: TenantTriggerInput): Promise<TenantTrigger> =>
    api.post<TenantTrigger>('/feeddrive/triggers', payload),
  updateTrigger: (id: string, payload: Partial<TenantTriggerInput>): Promise<TenantTrigger> =>
    api.patch<TenantTrigger>(`/feeddrive/triggers/${id}`, payload),
  setTriggerPaused: (id: string, paused: boolean): Promise<TenantTrigger> =>
    api.patch<TenantTrigger>(`/feeddrive/triggers/${id}/pause`, { paused }),
  deleteTrigger: async (id: string): Promise<void> => {
    await api.delete<unknown>(`/feeddrive/triggers/${id}`);
  },
  getCurations: (): Promise<TenantCuration[]> => api.get<TenantCuration[]>('/feeddrive/curations'),
  createCuration: (payload: TenantCurationInput): Promise<TenantCuration> =>
    api.post<TenantCuration>('/feeddrive/curations', payload),
  updateCuration: (id: string, payload: Partial<TenantCurationInput>): Promise<TenantCuration> =>
    api.patch<TenantCuration>(`/feeddrive/curations/${id}`, payload),
  setCurationEnabled: (id: string, enabled: boolean): Promise<TenantCuration> =>
    api.patch<TenantCuration>(`/feeddrive/curations/${id}/enabled`, { enabled }),
  deleteCuration: async (id: string): Promise<void> => {
    await api.delete<unknown>(`/feeddrive/curations/${id}`);
  },
  getAutomationRuns: (
    filters: {
      automationId?: string;
      status?: AutomationRunStatus;
      limit?: number;
    } = {},
  ): Promise<AutomationRun[]> => {
    const query = new URLSearchParams();
    if (filters.automationId) query.set('automation_id', filters.automationId);
    if (filters.status) query.set('status', filters.status);
    if (filters.limit) query.set('limit', String(filters.limit));
    const suffix = query.toString();
    return api.get<AutomationRun[]>(`/feeddrive/automation-runs${suffix ? `?${suffix}` : ''}`);
  },
  getAutomationRun: (id: string): Promise<AutomationRun> =>
    api.get<AutomationRun>(`/feeddrive/automation-runs/${id}`),
  dryRunAutomation: (id: string, cursor?: string): Promise<AutomationRun> =>
    api.post<AutomationRun>(`/feeddrive/automations/${id}/dry-run`, cursor ? { cursor } : {}),
  getAutomationCostReport: (automationId?: string): Promise<AutomationCostReport> => {
    const query = automationId ? `?automation_id=${encodeURIComponent(automationId)}` : '';
    return api.get<AutomationCostReport>(`/feeddrive/automation-runs/cost-report${query}`);
  },
};
